import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Eye, EyeOff } from "lucide-react";
import For from "../../images/about/log_art.jpg";
import { useAuth } from "../../contexts/AuthContext";
import toast from 'react-hot-toast';

const base_url = process.env.REACT_APP_BASE_URL;

function Login({ show, onClose, onLoginSuccess }) {
  const [showPassword, setShowPassword] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState(0); // 0: not shown, 1: email input, 2: OTP input
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState("");
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const { login } = useAuth();

  const loginValidationSchema = Yup.object().shape({
    email: Yup.string().email("Invalid email").required("Email is required"),
    password: Yup.string().required("Password is required"),
  });

  const forgotPasswordEmailSchema = Yup.object().shape({
    email: Yup.string().email("Invalid email").required("Email is required"),
  });

  const forgotPasswordOtpSchema = Yup.object().shape({
    otp: Yup.string().required("OTP is required"),
  });

  const resetPasswordSchema = Yup.object().shape({
    newPassword: Yup.string()
      .min(6, "Password must be at least 6 characters")
      .required("New password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword'), null], "Passwords must match")
      .required("Please confirm your password"),
  });

  const handleLogin = async (values, { setSubmitting, setErrors }) => {    
    try {      
      const response = await fetch(`${base_url}/api/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(values),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Use the login function from AuthContext
        await login(data.token);
        toast.success("Login successful!");
        onClose();
        if (onLoginSuccess) {
          onLoginSuccess();
        }
      } else {
        if (data.message === "Invalid password") {
          setErrors({ password: "Invalid password" });
          toast.error("Invalid password");
        } else if (data.message === "Request pending approval") {
          setErrors({ email: "Your account is pending approval. Please wait for admin confirmation." });
          toast.error("Account pending approval");
        } else if (data.message === "User not found") {
          setErrors({ email: "No account found with this email address" });
          toast.error("No account found with this email");
        } else {
          setErrors({ email: data.message || "Login failed" });
          toast.error("Login failed");
        }
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error("Error during login");
      setErrors({ email: "Something went wrong. Please try again later." });
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotPasswordEmail = async (values, { setSubmitting, setFieldError }) => {
    try {
      const response = await fetch(`${base_url}/api/auth/forgotPassword`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: values.email }),
      });
      const data = await response.json();
      if (response.ok) {
        setForgotPasswordEmail(values.email);
        setForgotPasswordStep(2); // Move to OTP step
        setForgotPasswordMessage("OTP has been sent to your email.");
      } else {
        setFieldError('email', data.error || "Failed to send OTP. Please try again.");
      }
    } catch (error) {
      console.error("Error during forgot password:", error);
      setFieldError('email', "Something went wrong. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotPasswordOtp = async (values, { setSubmitting, setFieldError }) => {
    try {
      const response = await fetch(`${base_url}/api/auth/verifyOtp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: forgotPasswordEmail,
          otp: values.otp
        }),
      });
      const data = await response.json();
      if (response.ok) {
        // Move to password reset step
        setForgotPasswordStep(3);
        setForgotPasswordMessage("OTP verified. Please enter your new password.");
      } else {
        setFieldError('otp', data.error || "Invalid OTP. Please try again.");
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      setFieldError('otp', "Something went wrong. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdatePassword = async (values, { setSubmitting, setFieldError }) => {
    try {
      const response = await fetch(`${base_url}/api/auth/updatePassword`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: forgotPasswordEmail,
          password: values.newPassword
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success("Password updated successfully!");
        // Reset the form and go back to login
        setForgotPasswordStep(0);
        setForgotPasswordMessage("");
      } else {
        setFieldError('newPassword', data.error || "Failed to update password. Please try again.");
      }
    } catch (error) {
      console.error("Error updating password:", error);
      setFieldError('newPassword', "Something went wrong. Please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  const initialValues = forgotPasswordStep === 1 
    ? { email: "" } 
    : forgotPasswordStep === 2 
      ? { otp: "" } 
      : forgotPasswordStep === 3
        ? { newPassword: "", confirmPassword: "" }
        : { email: "", password: "" };

  const getValidationSchema = () => {
    if (forgotPasswordStep === 1) return forgotPasswordEmailSchema;
    if (forgotPasswordStep === 2) return forgotPasswordOtpSchema;
    if (forgotPasswordStep === 3) return resetPasswordSchema;
    return loginValidationSchema;
  };

  const handleSubmit = (values, formikBag) => {
    if (forgotPasswordStep === 1) return handleForgotPasswordEmail(values, formikBag);
    if (forgotPasswordStep === 2) return handleForgotPasswordOtp(values, formikBag);
    if (forgotPasswordStep === 3) return handleUpdatePassword(values, formikBag);
    return handleLogin(values, formikBag);
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      dialogClassName="signup-modal-full w-100 custom-modal"
      size="md"
    >
      <div className="d-flex w-100">
        <div className="w-50 d-none d-md-block">
          <img
            src={For}
            alt="About Us"
            className="h-100 w-100 object-fit-cover"
          />
        </div>

        <div className="w-100 w-md-50 d-flex flex-column justify-content-between p-4 position-relative bg-white">
          <div>
            <h3 className="mb-4">
              {forgotPasswordStep === 1 
                ? "Forgot Password" 
                : forgotPasswordStep === 2 
                  ? "Verify OTP" 
                  : forgotPasswordStep === 3
                    ? "Reset Password"
                    : "Login"}
            </h3>

            <Formik
              initialValues={initialValues}
              validationSchema={getValidationSchema()}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting }) => (
                <Form>
                  {forgotPasswordStep === 1 ? (
                    <>
                      <div className="mb-3">
                        <p>Enter your email address and we'll send you an OTP to reset your password.</p>
                      </div>
                      
                      <div className="mb-3">
                        <label className="mb-2">Email</label>
                        <Field
                          name="email"
                          type="email"
                          className="form-control"
                          placeholder="Enter your email"
                        />
                        <ErrorMessage
                          name="email"
                          component="div"
                          className="text-danger"
                        />
                      </div>

                      <div className="d-grid gap-2">
                        <button 
                          type="submit" 
                          className="btn btn-primary"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? 'Sending OTP...' : 'Send OTP'}
                        </button>
                        
                        <button
                          type="button"
                          onClick={() => {
                            setForgotPasswordStep(0);
                            setForgotPasswordMessage("");
                          }}
                          className="btn btn-link"
                        >
                          Back to Login
                        </button>
                      </div>
                    </>
                  ) : forgotPasswordStep === 2 ? (
                    <>
                      <div className="mb-3">
                        <p>We've sent an OTP to {forgotPasswordEmail}. Please check your email.</p>
                        <label className="mb-2">Enter OTP</label>
                        <Field
                          name="otp"
                          type="text"
                          className="form-control"
                          placeholder="Enter 6-digit OTP"
                          maxLength="6"
                        />
                        <ErrorMessage
                          name="otp"
                          component="div"
                          className="text-danger"
                        />
                      </div>

                      {forgotPasswordMessage && (
                        <div className={`mb-3 ${forgotPasswordMessage.includes('successfully') ? 'text-success' : 'text-danger'}`}>
                          {forgotPasswordMessage}
                        </div>
                      )}

                      <div className="d-grid gap-2">
                        <button 
                          type="submit" 
                          className="btn btn-primary"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? 'Verifying...' : 'Verify OTP'}
                        </button>
                        
                        <div className="d-flex justify-content-between">
                          <button
                            type="button"
                            onClick={() => {
                              setForgotPasswordStep(1);
                              setForgotPasswordMessage("");
                            }}
                            className="btn btn-link p-0"
                          >
                            Back
                          </button>
                          
                          <button
                            type="button"
                            onClick={() => {
                              setForgotPasswordStep(0);
                              setForgotPasswordMessage("");
                            }}
                            className="btn btn-link p-0"
                          >
                            Back to Login
                          </button>
                        </div>
                      </div>
                    </>
                  ) : forgotPasswordStep === 3 ? (
                    <>
                      <div className="mb-3">
                        <p>Enter your new password.</p>
                      </div>
                      <div className="mb-3">
                        <label className="mb-2">New Password</label>
                        <Field
                          name="newPassword"
                          type="password"
                          className="form-control"
                          placeholder="Enter new password"
                        />
                        <ErrorMessage
                          name="newPassword"
                          component="div"
                          className="text-danger"
                        />
                      </div>
                      <div className="mb-3">
                        <label className="mb-2">Confirm Password</label>
                        <Field
                          name="confirmPassword"
                          type="password"
                          className="form-control"
                          placeholder="Confirm new password"
                        />
                        <ErrorMessage
                          name="confirmPassword"
                          component="div"
                          className="text-danger"
                        />
                      </div>

                      <div className="d-grid gap-2">
                        <button 
                          type="submit" 
                          className="btn btn-primary"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? 'Updating...' : 'Update Password'}
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="mb-3">
                        <label className="mb-2">Email</label>
                        <Field
                          name="email"
                          type="email"
                          className="form-control"
                          placeholder="Enter your email"
                        />
                        <ErrorMessage
                          name="email"
                          component="div"
                          className="text-danger"
                        />
                      </div>

                      <div className="mb-3 position-relative">
                        <label className="mb-2">Password</label>
                        <Field
                          name="password"
                          type={showPassword ? "text" : "password"}
                          className="form-control pe-5"
                          placeholder="Enter password"
                        />
                        <span
                          onClick={() => setShowPassword(!showPassword)}
                          style={{
                            position: "absolute",
                            top: "38px",
                            right: "15px",
                            cursor: "pointer",
                          }}
                        >
                          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </span>
                        <ErrorMessage
                          name="password"
                          component="div"
                          className="text-danger"
                        />
                      </div>

                      <div className="text-end mb-3">
                        <button
                          type="button"
                          onClick={() => setForgotPasswordStep(1)}
                          className="btn btn-link p-0"
                          style={{ fontSize: "14px" }}
                        >
                          Forgot password?
                        </button>
                      </div>

                      <div className="text-end d-flex gap-2 justify-content-end">
                        <button
                          type="button"
                          onClick={onClose}
                          className="btn login-btn text-white position-relative overflow-hidden"
                          style={{ fontSize: "15px", fontFamily: "inherit" }}
                        >
                          <div className="swift-btn_animation d-flex justify-content-center align-items-center log_cus">
                            <p className="m-0 text-black">Close</p>
                          </div>
                        </button>

                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="btn login-btn text-white position-relative overflow-hidden border-0"
                        >
                          <div
                            className="swift-btn_animation d-flex justify-content-center align-items-center"
                            style={{
                              background: "radial-gradient(circle, rgb(0, 0, 0), rgb(0, 0, 0))",
                            }}
                          >
                            <p className="m-0 text-black fw-bold button_name">
                              {isSubmitting ? "Logging in..." : "Login"}
                            </p>
                          </div>
                        </button>
                      </div>
                    </>
                  )}
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </Modal>
  );
}

export default Login;